import { Observable, BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { RoleService } from '../api/role.api.service';
import { RoleModel, RoleSavePayload } from '../../models/role/role.model';
import { List } from 'immutable';
import { AlertService } from '../alert.service';
import { BaseDataStore } from './base-data.store';
import { GroupStore } from './group.store';
import { map, shareReplay } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { PermissionService } from '../permission.service';
import * as PermissionConstants from '../../modules/catch-core/data/constants/permission.constants';

@Injectable({
  providedIn: 'root',
})
export class RoleStore extends BaseDataStore {
  private _roles: BehaviorSubject<List<RoleModel>> = new BehaviorSubject(List<RoleModel>([]));
  public $roles: Observable<List<RoleModel>> = this._roles.asObservable();

  constructor(
    private roleService: RoleService,
    private alertService: AlertService,
    private groupStore: GroupStore,
    private permissionService: PermissionService,
  ) {
    super();
    this.behaviorSubjectList.push(this._roles);
  }

  public loadInitialData(organizationId?: string | null): void {
    if (this.subscribers.roleSubscription) {
      this.subscribers.roleSubscription.unsubscribe();
    }

    if (!this.permissionService.hasPermission(PermissionConstants.ROLE_READ)) {
      // Non-admin roles don't hold role:read, so GET api/roles would 403 -
      // see api/docs/permissions/permission-v3.md.
      this._roles.next(List([]));
      return;
    }

    this.subscribers.roleSubscription = this.roleService.getRoles(organizationId).subscribe(
      (res: any) => {
        const roles = (<Object[]>res).map((role: any) => new RoleModel(role));
        this._roles.next(List(roles));
      },
      (err: any) => {
        console.log('Error retrieving Roles');
        console.log(err);
        this.alertService.showError('Error retrieving Roles!');
      },
    );
  }

  public $updateRolePermissions(roleId: string, permissionIds: string[]): Observable<boolean> {
    if (!roleId || !permissionIds) {
      return throwError(() => new Error('roleId/permissionIds required'));
    }
    return this.roleService.updateRolePermissions(roleId, permissionIds).pipe(
      map(() => {
        this.loadInitialData();
        return true;
      }),
    );
  }

  public $addRole(payload: RoleSavePayload): Observable<RoleModel> {
    if (!payload) {
      return throwError(() => new Error('payload required'));
    }
    const obs = this.roleService.addRole(payload).pipe(
      map((res: any) => new RoleModel(res)),
      shareReplay(1),
    );

    obs.subscribe((role: RoleModel) => {
      const roles = this._roles.getValue();
      this._roles.next(roles.push(role));
      this.loadInitialData();
      this.groupStore.loadInitialData();
    });

    return obs;
  }

  public $updateRole(roleId: string, payload: RoleSavePayload): Observable<RoleModel> {
    if (!roleId || !payload) {
      return throwError(() => new Error('roleId/payload required'));
    }
    const obs = this.roleService.updateRole(roleId, payload).pipe(
      map((res: any) => new RoleModel(res)),
      shareReplay(1),
    );

    obs.subscribe(() => {
      this.loadInitialData();
      this.groupStore.loadInitialData();
    });

    return obs;
  }

  public $deleteRole(roleId: string): Observable<boolean> {
    if (!roleId) {
      return of(false);
    }
    return this.roleService.deleteRole(roleId).pipe(
      map(() => {
        const roles = this._roles.getValue();
        this._roles.next(roles.filter((r) => r?.id !== roleId).toList());
        this.groupStore.loadInitialData();
        return true;
      }),
    );
  }
}
